import { CustomFee, DefaultValuesTypes, Event, EventDateType } from './types';
import { DateSchema, array, boolean, date, number, object, ref, string } from 'yup';
import { EVENT_MODE_NORMAL, EVENT_MODE_PUNCHUP, EVENT_MODE_REGISTRATION, ZERO_TIME } from 'utils/constants';

import { EventMode } from 'utils/types';
import { canonicalTimezone } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupTimeZone/utils';
import { convertUTCToDate } from 'utils/utils';
import i18n from 'locals/i18n';
import { yupResolver } from '@hookform/resolvers/yup';

export const defaultValues: DefaultValuesTypes = {
	name: '',
	venue: '',
	location: '',
	description: '',
	selling_time: null,
	dates: [],
	custom_fees: [],
	timezone: '',
	gate_time: '',
	postal_code: '',
	isMultiDay: false,
	ticketing_mode: 'dynamic',
	custom_email_message: '',
	support: '',
	mode: EVENT_MODE_NORMAL,
	quantity: '0',
	approval_required: false,
	door_time: null,
	protect_with_password: false,
	password: '',
	confirm_password: '',
	change_password: false,
	want_to_change_password: false,
	remove_password: false,
	seats_chart_key: undefined,
	enforce_no_orphan_seats: false,
	presale_code: '',
	always_show_tickets: false,
	on_sale: true,
	inactive: false,
	adjust_ticket_dates: false,
	hide_event: false,
	hidden_until: null,
};

export const getFormDetails = (eventMode: EventMode) => {
	const sharedDetails = {
		name: string().required(i18n.t('editEvent-requiredName')),
		location: string().required(i18n.t('editEvent-requiredLocation')),
		timezone: string().required(i18n.t('editEvent-requiredTimezone')),
		custom_fees: array(
			object({
				name: string().required('Required'),
				amount: number()
					.required()
					.when('type', {
						is: 'percentage',
						then: number().min(0, 'Must be a number between 0 and 100').max(100, 'Must be a number between 0 and 100'),
					}),
			}),
		),
		dates: array(
			object({
				start_date: date().required(),
				end_date: date()
					.required()
					.when('start_date', (start_date: Date, schema: DateSchema) =>
						schema.min(start_date, i18n.t('error-endDate')),
					),
			}),
		),
		seats_chart_key: string().optional().nullable(),
		enforce_no_orphan_seats: boolean().optional(),
		presale_code: string().optional(),
		always_show_tickets: boolean().optional(),
		on_sale: boolean().optional(),
		inactive: boolean().optional(),
	};

	const normalSchema = object().shape({
		...sharedDetails,
		postal_code: string(),
	});

	const registrationSchema = object().shape({
		...sharedDetails,
		quantity: string().required(i18n.t('editEvent-requiredQuantity')),
	});

	const punchupSchema = object().shape({
		name: string().min(3, i18n.t('editEvent-requiredName')).required(i18n.t('editEvent-requiredName')),
		/* why nullable().required(): a legacy show can hold null; .nullable() lets required() report
		   "Timezone is required" instead of yup's raw "must be a `string` type" typeError. */
		timezone: string().nullable().required(i18n.t('editEvent-requiredTimezone')),
		door_time: date()
			.optional()
			/* why dates[0].start_date, not start_date: CLAUDE.md → invariant 8 */
			.when('dates[0].start_date', (start_date: Date, schema: DateSchema) => {
				if (start_date) {
					return schema.max(start_date, i18n.t('error-doorTime-before-startDate'));
				}
				return schema;
			})
			.nullable()
			.typeError('Invalid Date'),
		dates: array(
			object({
				start_date: date().required(i18n.t('editEvent-requiredStartDate')),
				end_date: date()
					.required()
					.when('start_date', (start_date: Date, schema: DateSchema) =>
						schema.min(start_date, i18n.t('error-endDate')),
					),
			}),
		),
		change_password: boolean(),
		want_to_change_password: boolean(),
		password: string().when('want_to_change_password', {
			is: true,
			then: (schema) =>
				schema.required(i18n.t('Password is required')).min(8, i18n.t('Password must be at least 8 characters')),
			otherwise: (schema) => schema.notRequired(),
		}),
		confirm_password: string().when('want_to_change_password', {
			is: true,
			then: (schema) =>
				schema
					.required(i18n.t('Please confirm your password'))
					.oneOf([ref('password')], i18n.t('Passwords must match')),
			otherwise: (schema) => schema.notRequired(),
		}),
		hide_event: boolean(),
		hidden_until: date()
			.nullable()
			.when('hide_event', {
				is: true,
				then: (schema) => schema.required(i18n.t('Hidden until date is required')),
				otherwise: (schema) => schema.notRequired(),
			}),
	});

	if (eventMode === EVENT_MODE_REGISTRATION) {
		return yupResolver(registrationSchema);
	} else if (eventMode === EVENT_MODE_PUNCHUP) {
		return yupResolver(punchupSchema);
	} else {
		return yupResolver(normalSchema);
	}
};

/* why undefined, not an Invalid Date: CLAUDE.md → Inbound date parse */
const toEventDate = (value: string | null | undefined, timeZone: string): Date | undefined => {
	if (!value || value === ZERO_TIME) return undefined;
	const parsed = new Date(convertUTCToDate(value, timeZone));
	return isNaN(parsed.getTime()) ? undefined : parsed;
};

export const handleInitialValues = (event: Event) => {
	const dates = event.days.map(({ start_date, end_date, id }: EventDateType) => ({
		start_date: toEventDate(start_date, event?.time_zone),
		end_date: toEventDate(end_date, event?.time_zone),
		id,
	}));

	const selling_time =
		event.selling_time === ZERO_TIME ? event.selling_time : convertUTCToDate(event.selling_time, event.time_zone);

	const custom_fees = event.custom_fees?.map(({ ID, name, amount, type, taxable, general }: CustomFee) => ({
		ID,
		name,
		amount,
		type,
		taxable,
		general,
	}));

	const hidden_until = toEventDate(event.hidden_until, event.time_zone) ?? null;

	const allValues: DefaultValuesTypes = {
		name: event.name,
		location: event.street_address,
		venue: event.venue,
		description: event.description,
		selling_time,
		dates: dates,
		timezone: canonicalTimezone(event.time_zone),
		gate_time: event.gate_time ? event.gate_time : '',
		postal_code: event.postal_code,
		isMultiDay: event.days.length > 1,
		ticketing_mode: event.ticketing_mode,
		custom_fees: custom_fees || [],
		custom_email_message: event.custom_email_message,
		support: event.support_contact?.[0]?.contact || '',
		mode: event.mode,
		quantity: `${event.quantity}`,
		approval_required: event.approval_required,
		door_time: toEventDate(event.door_time, event.time_zone) ?? null,
		protect_with_password: event.has_password || false,
		password: '',
		confirm_password: '',
		change_password: event.has_password || false,
		want_to_change_password: false,
		remove_password: false,
		seats_chart_key: event.seats_chart_key,
		enforce_no_orphan_seats: event.enforce_no_orphan_seats ?? false,
		presale_code: event.presale_code || '',
		always_show_tickets: event.always_show_tickets || false,
		on_sale: event.on_sale ?? true,
		inactive: event.inactive ?? false,
		adjust_end_date: true,
		adjust_ticket_dates: false,
		hide_event: !!event.hidden_until,
		hidden_until: hidden_until,
	};
	return allValues;
};
